import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  CheckCircle2, 
  Sparkles, 
  Building2, 
  TrendingUp, 
  Eye, 
  EyeOff, 
  X, 
  ArrowRight,
  Award,
  KeyRound,
  Check,
  Zap,
  Info
} from 'lucide-react';
import { 
  KNOWN_COHORT_PRESETS, 
  CohortPreset, 
  activateCohortByCode, 
  CohortAccessSettings 
} from '../utils/cohortService';

interface CohortOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActivated: (settings: CohortAccessSettings) => void;
  initialCode?: string;
  showToast?: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
}

export function CohortOnboardingModal({
  isOpen,
  onClose,
  onActivated,
  initialCode = '',
  showToast
}: CohortOnboardingModalProps) {
  const [inputCode, setInputCode] = useState(initialCode || 'PILOT-ALPHA');
  const [selectedPreset, setSelectedPreset] = useState<CohortPreset | null>(
    KNOWN_COHORT_PRESETS.find(p => p.code === (initialCode || 'PILOT-ALPHA')) || KNOWN_COHORT_PRESETS[0]
  );
  const [agreedToTerms, setAgreedToTerms] = useState(true);

  if (!isOpen) return null;

  const handleSelectPreset = (preset: CohortPreset) => {
    setSelectedPreset(preset);
    setInputCode(preset.code);
  };

  const handleConfirm = () => {
    const res = activateCohortByCode(inputCode || selectedPreset?.code || 'PILOT-ALPHA');
    if (res.success) {
      if (showToast) showToast(res.message, 'success');
      onActivated(res.settings);
      onClose();
    } else {
      if (showToast) showToast(res.message, 'error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-[#0C0E0F]/85 backdrop-blur-md animate-fade-in">
      <div className="absolute inset-0 cursor-pointer" onClick={onClose} />

      <div 
        className="relative w-full max-w-xl bg-[#151819] border border-[#2A2F31] rounded-2xl shadow-2xl overflow-hidden flex flex-col z-10 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* TOP ACCENT GRADIENT */}
        <div className="h-1.5 w-full bg-gradient-to-r from-teal-500 via-emerald-400 to-teal-600" />

        {/* HEADER */}
        <div className="p-5 border-b border-[#1F2426] flex items-center justify-between bg-[#0C0E0F]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/15 border border-teal-500/30 flex items-center justify-center text-teal-400 shrink-0">
              <Award size={22} />
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-teal-500/10 text-teal-300 text-[10px] font-bold uppercase tracking-wider mb-0.5">
                🎓 Institutional License Onboarding
              </div>
              <h3 className="text-lg font-bold text-white leading-tight">
                Cohort Sponsorship &amp; Health Sharing
              </h3>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-[#1F2426] text-gray-400 hover:text-white flex items-center justify-center transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* BODY */}
        <div className="p-5 sm:p-6 space-y-5 overflow-y-auto max-h-[75vh]">
          
          {/* COHORT PRESET SELECTOR / CODE INPUT */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center justify-between">
              <span>Select or Enter Cohort Voucher Code</span>
              <span className="text-[10px] text-teal-400 font-normal">Sponsored Seat</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {KNOWN_COHORT_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handleSelectPreset(preset)}
                  className={`p-2.5 rounded-xl border text-left transition-all ${
                    selectedPreset?.id === preset.id
                      ? 'bg-teal-950/30 border-teal-500/60 ring-1 ring-teal-500/40'
                      : 'bg-[#0C0E0F] border-[#2A2F31] hover:border-gray-600 text-gray-300'
                  }`}
                >
                  <div className="text-xs font-bold text-white truncate">{preset.name}</div>
                  <div className="text-[10px] text-teal-400 font-mono mt-0.5">{preset.code}</div>
                </button>
              ))}
            </div>

            <div className="relative mt-2">
              <input
                type="text"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                placeholder="Enter custom accelerator code (e.g. PILOT-ALPHA)"
                className="w-full bg-[#0C0E0F] border border-[#2A2F31] focus:border-teal-500 rounded-xl px-3.5 py-2.5 text-xs text-white uppercase font-mono tracking-wider focus:outline-none"
              />
            </div>
          </div>

          {/* THE TRANSPARENCY CARD (BOARDY'S EXACT SPEC) */}
          <div className="p-4 rounded-xl bg-[#0C0E0F] border border-teal-500/30 space-y-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/5 rounded-full blur-2xl pointer-events-none" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-white">
                  {selectedPreset?.name || 'Venture Accelerator Cohort'}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 font-mono font-bold">
                  Sponsorship Active
                </span>
              </div>
              <span className="text-[11px] text-gray-400">
                Sponsored by <strong className="text-gray-200">{selectedPreset?.sponsor || 'Accelerator Partner'}</strong>
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              {/* WHAT THE PROGRAM SEES (ON BY DEFAULT) */}
              <div className="p-3 rounded-lg bg-[#151819] border border-emerald-500/30 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-400">
                  <Eye size={14} />
                  <span>Visible to Program Director</span>
                </div>
                <ul className="text-[11px] text-gray-300 space-y-1.5">
                  <li className="flex items-start gap-1.5">
                    <CheckCircle2 size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span><strong>Runway Health Band:</strong> Safe / Caution / Red traffic light.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <CheckCircle2 size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span><strong>Burn Trajectory:</strong> Monthly burn velocity &amp; runway months.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <CheckCircle2 size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span><strong>Audit Readiness:</strong> 5-regime statutory tax reserve status.</span>
                  </li>
                </ul>
              </div>

              {/* WHAT REMAINS 100% PRIVATE */}
              <div className="p-3 rounded-lg bg-[#151819] border border-purple-500/30 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-purple-400">
                  <Lock size={14} />
                  <span>100% Sovereign &amp; Locked</span>
                </div>
                <ul className="text-[11px] text-gray-300 space-y-1.5">
                  <li className="flex items-start gap-1.5">
                    <ShieldCheck size={12} className="text-purple-400 shrink-0 mt-0.5" />
                    <span><strong>Raw Bank Feeds:</strong> Encrypted client-side (AES-256).</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <ShieldCheck size={12} className="text-purple-400 shrink-0 mt-0.5" />
                    <span><strong>Customer Names:</strong> Air-gapped from cohort director.</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <ShieldCheck size={12} className="text-purple-400 shrink-0 mt-0.5" />
                    <span><strong>Deep Audit Room:</strong> OFF by default (you control access).</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* EXPLICIT NOTICE */}
            <div className="p-2.5 rounded-lg bg-teal-950/20 border border-teal-500/20 flex items-start gap-2 text-[11px] text-teal-200/90">
              <Info size={14} className="text-teal-400 shrink-0 mt-0.5" />
              <span>
                <strong>Cohort License Policy:</strong> High-level runway and burn sharing is active by default to populate your program's aggregated cohort heatmap. Detailed ledger lines remain private unless you explicitly generate a temporary Auditor Pass in Settings.
              </span>
            </div>
          </div>

          {/* FOUNDER CONSENT CHECKBOX */}
          <label className="flex items-center gap-3 p-3 rounded-xl bg-[#0C0E0F] border border-[#2A2F31] cursor-pointer hover:border-gray-600 transition-colors">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="w-4 h-4 rounded text-teal-500 focus:ring-teal-500 bg-[#151819] border-gray-600 accent-teal-500 cursor-pointer"
            />
            <span className="text-xs text-gray-300 leading-snug">
              I understand my cohort license sponsors this workspace and shares high-level runway health with <strong>{selectedPreset?.sponsor || 'the program'}</strong>.
            </span>
          </label>
        </div>

        {/* FOOTER */}
        <div className="p-4 border-t border-[#1F2426] bg-[#0C0E0F] flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!agreedToTerms}
            onClick={handleConfirm}
            className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              agreedToTerms
                ? 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-[#0C0E0F] shadow-lg shadow-teal-500/20 cursor-pointer'
                : 'bg-gray-800 text-gray-500 cursor-not-allowed'
            }`}
          >
            <span>Accept &amp; Enter Workspace</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
